import NavigationArrowIcon from "@/assets/icons/NavigationArrowIcon";
import { routes } from "@/routes/paths";
import { FocusAreaData } from "@/services/apitypes";
import { Box, Text, Group } from "@mantine/core";
import { Link } from "react-router-dom";
import OverlayStarToggle from "@/components/common/OverlayStarToggle";
import { usePriorityStore, createTopicId, SelectedTopic } from "@/stores/priorityStore";
import { surfaceInsetInteractiveRowClassName } from "@canucci/base-mantine/theme";
import styles from "./CanvasFocusAreas.module.css";

interface FocusAreasOwnProps {
  externalId: string;
  groupIndex: number;
  groupTitle: string;
  focusAreas: FocusAreaData[];
  isDisabled?: boolean;
  isParticipation?: boolean;
  votingEnabled?: boolean;
  isVotingClosed?: boolean;
}

type FocusAreasComponentProps = FocusAreasOwnProps & React.HTMLAttributes<HTMLUListElement>;

export function CanvasFocusAreas({
  externalId,
  groupIndex,
  groupTitle,
  focusAreas,
  isDisabled,
  isParticipation,
  votingEnabled,
  isVotingClosed = false,
  ...restUlProps
}: FocusAreasComponentProps) {
  const selectedTopicIds = usePriorityStore((state) => state.selectedTopicIds);
  const { toggleTopic } = usePriorityStore.getState();
  return (
    <Box component="ul" className={styles.list} {...restUlProps}>
      {focusAreas.map((focusArea: FocusAreaData, areaIndex: number) => {
        const focusAreaTitle = focusArea.text.title ?? `Topic ${areaIndex + 1}`;
        const topicId = focusArea.id ?? createTopicId(groupIndex, areaIndex);

        const isSelected = selectedTopicIds.includes(topicId);
        const isFirst = areaIndex === 0;
        const isLast = areaIndex === focusAreas.length - 1;

        const handleStarToggle = () => {
          const topic: SelectedTopic = {
            topicId,
            groupIndex,
            areaIndex,
            title: focusAreaTitle,
            groupTitle,
            label: groupTitle,
          };
          toggleTopic(topic);
        };

        return (
          <Box component="li" key={areaIndex} className={styles.item} data-last={isLast}>
            <Link
              className={surfaceInsetInteractiveRowClassName}
              data-first={isFirst}
              data-last={isLast}
              aria-disabled={isDisabled}
              to={routes.canvas.focusArea.path(externalId ?? "", groupIndex, areaIndex, isParticipation)}
              onClick={
                isDisabled
                  ? (e) => {
                      e.preventDefault();
                    }
                  : undefined
              }
            >
              <Group w="100%" gap="xs" align="center" wrap="nowrap">
                {votingEnabled && (
                  <Box className={styles.selectionControl}>
                    <OverlayStarToggle
                      isSelected={isSelected}
                      onToggle={handleStarToggle}
                      size="sm"
                      color="white"
                      disabled={isVotingClosed}
                      topicId={topicId}
                    />
                  </Box>
                )}
                <Text fw={700} fz="md" c="var(--text-on-dark)" className={styles.title}>
                  {focusAreaTitle}
                </Text>
                <Box className={styles.arrow}>
                  <NavigationArrowIcon direction="right" color="var(--text-on-dark)" />
                </Box>
              </Group>
            </Link>
          </Box>
        );
      })}
    </Box>
  );
}
